import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { DollarSign, Check, MessageSquare, Download, Filter, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const pendingQuotes = [
  { id: 1, title: 'The Wonders of Coding', level: 'Sec', category: 'NF EL', supplier: 'OpenTrolley', price: 18.50, isLowest: true, stock: 500, stockOk: true, justification: '—' },
  { id: 2, title: 'Secondary Science Digest', level: 'Sec', category: 'NF EL', supplier: 'Popular Books', price: 15.20, isLowest: false, lowestPrice: 14.80, lowestSupplier: 'Times', stock: 420, stockOk: true, justification: 'Times stock 80 — insufficient; Popular 420 recommended despite higher price' },
  { id: 3, title: 'Tamil Literature Anthology', level: 'Sec', category: 'MTL-TL', supplier: 'Grassroots', price: 14.00, isLowest: true, stock: 80, stockOk: false, justification: 'Reprint confirmed in 8 wks; EPH backup at $14.50' },
  { id: 4, title: 'Malay Folklore Vol 4', level: 'Pri', category: 'MTL-ML', supplier: 'OpenTrolley', price: 12.40, isLowest: true, stock: 200, stockOk: true, justification: '—' },
];

const approvedQuotes = [
  { id: 101, title: 'Between Two Worlds', level: 'Sec', category: 'Fiction EL', supplier: 'OpenTrolley', price: 16.80, approvedDate: '28 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 102, title: 'The Last Library', level: 'Sec', category: 'Fiction EL', supplier: 'Times Books', price: 15.90, approvedDate: '28 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 103, title: 'Code Red', level: 'Sec', category: 'Fiction EL', supplier: 'OpenTrolley', price: 17.20, approvedDate: '27 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 104, title: 'Singapore Stories Vol 3', level: 'All', category: 'NF EL', supplier: 'OpenTrolley', price: 22.50, approvedDate: '27 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 105, title: 'Roots & Routes', level: 'Sec', category: 'NF EL', supplier: 'Times Books', price: 24.00, approvedDate: '26 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 106, title: 'Climate Change & You', level: 'Sec', category: 'NF EL', supplier: 'OpenTrolley', price: 14.20, approvedDate: '26 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 107, title: 'Semangat!', level: 'Sec', category: 'MTL-ML', supplier: 'Grassroots', price: 16.80, approvedDate: '25 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 108, title: 'The Memory Keeper', level: 'Sec', category: 'Fiction EL', supplier: 'Penguin', price: 16.80, approvedDate: '25 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
];

const queriedQuotes = [
  { id: 201, title: 'Tamil Literature Anthology', level: 'Sec', category: 'MTL-TL', supplier: 'Grassroots', price: 14.00, query: 'Stock of 80 units insufficient for ~156 secondary schools. Confirm alternative plan.', queryDate: '27 Apr 2026', response: 'Grassroots confirms reprint in 8 weeks. EPH backup (120 units @ $14.50). Price locked at $14.00.', responseDate: '28 Apr 2026', status: 'resolved' },
  { id: 202, title: 'Math Made Fun', level: 'Pri', category: 'NF EL', supplier: 'EPH', price: 13.50, query: 'Price increase from $12.80 last cycle. Please justify.', queryDate: '26 Apr 2026', response: 'New edition with additional chapters. Price increase approved by publisher.', responseDate: '27 Apr 2026', status: 'resolved' },
  { id: 203, title: 'Daughters of Singapore', level: 'Sec', category: 'Fiction EL', supplier: 'Epigram', price: 16.50, query: 'Delivery timeline unclear. Confirm can meet Jan 2028 deadline.', queryDate: '25 Apr 2026', response: null, responseDate: null, status: 'pending' },
];

export default function MOEQuoteApproval() {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedQuery, setSelectedQuery] = useState(null);

  const handleApprove = (id) => {
    toast.success('Quote approved');
  };

  const handleBulkApprove = () => {
    toast.success('32 quotes approved successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <DollarSign className="w-7 h-7 text-[#1A3C6E]" />
            Supplier Quote Review & Approval
          </h1>
          <p className="text-slate-500 mt-1">CDS Cycle 1 2028 | 38 pending | 156 approved</p>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            Civica has submitted 194 title recommendations. 38 require your review before the Acquisition Module opens to schools.
          </p>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" data-testid="tab-pending-quotes">Pending Approval (38)</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-quotes">Approved (156)</TabsTrigger>
          <TabsTrigger value="queries" data-testid="tab-queries">Queries Raised (3)</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Pending Approval Tab */}
        <TabsContent value="pending" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Quotes Pending Approval</CardTitle>
                <CardDescription>Review supplier quotes and Civica recommendations</CardDescription>
              </div>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleBulkApprove}
                data-testid="bulk-approve-quotes"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve All Uncomplicated (32 titles)
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Cat</TableHead>
                    <TableHead>Rec. Supplier</TableHead>
                    <TableHead>Rec. Price</TableHead>
                    <TableHead>Is Lowest?</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Justification</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingQuotes.map((quote, idx) => (
                    <TableRow key={quote.id} className="table-row-striped">
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-medium">{quote.title}</TableCell>
                      <TableCell>{quote.level}</TableCell>
                      <TableCell>{quote.category}</TableCell>
                      <TableCell>{quote.supplier}</TableCell>
                      <TableCell>${quote.price.toFixed(2)}</TableCell>
                      <TableCell>
                        {quote.isLowest ? (
                          <Badge className="bg-emerald-50 text-emerald-700">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700">
                            No (${quote.lowestPrice} {quote.lowestSupplier})
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={quote.stockOk ? 'text-emerald-600' : 'text-amber-600'}>
                          {quote.stock} {quote.stockOk ? '✓' : '⚠️'}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <span className="text-xs text-slate-600">{quote.justification}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprove(quote.id)}
                            data-testid={`approve-quote-${quote.id}`}
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedQuery(quote.id)}
                          >
                            <MessageSquare className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 4 of 38 pending quotes</p>
            </CardContent>
          </Card>

          {/* Query Panel */}
          {selectedQuery && (
            <Card className="mt-4 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base">Query: Tamil Literature Anthology</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-white rounded-lg border">
                  <p className="text-sm font-medium text-slate-700 mb-1">MOE HQ Query sent:</p>
                  <p className="text-sm text-slate-600">"Stock of 80 units insufficient for ~156 secondary schools. Confirm alternative plan."</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-700 mb-1">Civica Response:</p>
                  <p className="text-sm text-emerald-600">"Grassroots confirms reprint in 8 weeks. EPH backup (120 units @ $14.50). Price locked at $14.00."</p>
                </div>
                <div className="flex gap-2">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="w-4 h-4 mr-1" /> Accept & Approve
                  </Button>
                  <Button variant="outline" className="text-red-600">
                    Reject — Require New Quote
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Approved Quotes (156)</CardTitle>
              </div>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Full List
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Approved</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedQuotes.map((quote) => (
                    <TableRow key={quote.id} className="table-row-striped">
                      <TableCell className="font-medium">{quote.title}</TableCell>
                      <TableCell>{quote.level}</TableCell>
                      <TableCell>{quote.category}</TableCell>
                      <TableCell>{quote.supplier}</TableCell>
                      <TableCell>${quote.price.toFixed(2)}</TableCell>
                      <TableCell>{quote.approvedDate}</TableCell>
                      <TableCell>{quote.approvedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 8 of 156 approved quotes</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Queries Raised Tab */}
        <TabsContent value="queries" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Queries Raised (3)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {queriedQuotes.map((quote) => (
                  <div key={quote.id} className={`p-4 border rounded-lg ${quote.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'bg-slate-50'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{quote.title}</h4>
                        <p className="text-sm text-slate-500">{quote.level} | {quote.category} | {quote.supplier} | ${quote.price.toFixed(2)}</p>
                      </div>
                      {quote.status === 'resolved' ? (
                        <Badge className="bg-emerald-50 text-emerald-700">Resolved</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700">Pending Response</Badge>
                      )}
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                      <p className="text-sm font-medium text-blue-800 mb-1">MOE HQ Query ({quote.queryDate}):</p>
                      <p className="text-sm text-blue-700">{quote.query}</p>
                    </div>
                    {quote.response && (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm font-medium text-emerald-800 mb-1">Civica Response ({quote.responseDate}):</p>
                        <p className="text-sm text-emerald-700">{quote.response}</p>
                      </div>
                    )}
                    {quote.status === 'resolved' && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                          <Check className="w-4 h-4 mr-1" /> Accept & Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600">
                          Reject — Request New Quote
                        </Button>
                      </div>
                    )}
                    {quote.status === 'pending' && (
                      <Button size="sm" variant="outline" className="mt-3">
                        <MessageSquare className="w-4 h-4 mr-1" /> Send Reminder to Civica
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Audit Trail</CardTitle>
                <CardDescription>Complete history of quote decisions</CardDescription>
              </div>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export Full Audit Log PDF
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Decision</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="table-row-striped">
                    <TableCell>28 Apr 2026</TableCell>
                    <TableCell>Climate Change & You</TableCell>
                    <TableCell>OpenTrolley</TableCell>
                    <TableCell>$16.80</TableCell>
                    <TableCell><Badge className="bg-emerald-50 text-emerald-700">Approved</Badge></TableCell>
                    <TableCell>Ms Lim Hui Shan</TableCell>
                    <TableCell className="text-xs">Lowest + adequate stock</TableCell>
                  </TableRow>
                  <TableRow className="table-row-striped">
                    <TableCell>28 Apr 2026</TableCell>
                    <TableCell>Secondary Science Digest</TableCell>
                    <TableCell>Popular</TableCell>
                    <TableCell>$15.20</TableCell>
                    <TableCell><Badge className="bg-emerald-50 text-emerald-700">Approved</Badge></TableCell>
                    <TableCell>Ms Lim Hui Shan</TableCell>
                    <TableCell className="text-xs">Stock justification accepted</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
